import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  DropZoneCore,
  IListPDF,
} from "src/Pages/components/DropZoneCore/DropZoneCore";
import { CardPreviewFile } from "src/Pages/components/CardPreviewFile/CardPreviewFile";
import { handleIfLimitCharacter } from "src/shared/Utils/LimitText";
import { ButtonCore } from "src/Pages/components/ButtonCore/ButtonCore";
import { UseFormComprovante } from "./Hooks/UseFeaturesFormulario";
import { Receipt } from "@mui/icons-material";
import { SendIcon } from "src/Pages/components/Icons/Icons";
import { api } from "src/shared/setup/API/api";
import { UseDelete } from "src/Pages/components/ModalDeleteCore/Hooks/UseDelete";
import { ModalDeleteCore } from "src/Pages/components";
import { useAuthContext } from "src/Contexts/AuthContext/authContext";

interface IFormComprovante {
  id: string;
  type: "COMPROVANTE_PAGAMENTO" | "NOTA_FISCAL";
}

export const FormComprovante = ({ id, type }: IFormComprovante) => {
  const { userPerfil } = useAuthContext();
  const { handleSubmit, loading } = UseFormComprovante({ id });

  const theme = useTheme();
  const isBreakPoint = useMediaQuery(theme.breakpoints.down("lg"));
  const [comprovantes, setComprovantes] = useState<
    {
      id: string;
      conta_corrente_id: string;
      original_name: string;
      mime_type: string;
      url: string;
      size: number;
      type: "COMPROVANTE_PAGAMENTO" | "NOTA_FISCAL";
      created_at: string;
      updated_at: string;
    }[]
  >([]);

  const {
    handleDelete,
    open: openModalDelete,
    loading: loadingDelete,
    setId: setIdModalDelete,
    setOpen: setOpenModalDelete,
  } = UseDelete({
    url: `/contracts-files`,
  });

  useEffect(() => {
    api.get(`/comprovantes-pagamentos/${id}`).then((res) => {
      setComprovantes(res.data.data);
    });
  }, [loading, loadingDelete]);

  const handleUploadFile = useCallback(async (files: IListPDF[]) => {
    handleSubmit({
      uploadComprovanteFiles: files,
      type,
    });
  }, []);

  const isComprovante = type === "COMPROVANTE_PAGAMENTO";
  const isProdutor = userPerfil?.type_user === "PRODUCER";

  return (
    <>
      <ModalDeleteCore
        loading={loadingDelete}
        modalOpen={openModalDelete}
        no="Não, Cancelar"
        titlePrimary="Você deseja excluir da lista ?"
        yes="Sim, Deletar !"
        onClickFalse={() => setOpenModalDelete(false)}
        onClickTrue={() => handleDelete()}
      />
      <Box
        sx={{
          mt: { xs: 2, sm: 4 },
          display:
            (isComprovante && !isProdutor) ||
            (type === "NOTA_FISCAL" && isProdutor)
              ? "flex"
              : "none",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          gap: { xs: 2, md: 2 },
        }}
      >
        <Box sx={{ maxWidth: "370px" }}>
          <Typography
            sx={{ fontSize: { xs: "14px", sm: "16px" }, color: "text.primary" }}
          >
            {isComprovante
              ? "Salve o comprovante de pagamento."
              : "Salve a nota fiscal."}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "13px" },
              color: "text.secondary",
            }}
          >
            {isComprovante
              ? "OBS: O comprovante poderá ser visualizado pelo produtor se o acesso dele estiver ativo ! Limite de 10MB por arquivo e máximo 5 arquivos."
              : "OBS: A nota fiscal poderá ser visualizada pelo Painel de controle da Corretora ! Limite de 10MB por arquivo e máximo 5 arquivos."}
          </Typography>
        </Box>

        <DropZoneCore
          onFilesChange={handleUploadFile}
          accept={{
            "image/jpeg": [],
            "image/png": [],
            "image/JPG": [],
            "image/WEBP": [],
            "image/GIF": [],

            "application/pdf": [],
            "application/msword": [],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
              [],
            "application/vnd.ms-excel": [],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
              [],
            "text/plain": [],
          }}
          sxStyle={{ display: "inline-block" }}
        >
          <ButtonCore
            color="primary"
            endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            title={isComprovante ? "Anexar comprovante" : "Anexar nota fiscal"}
            disabled={loading}
          />
        </DropZoneCore>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 4,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Receipt
              sx={{
                fontSize: "24px",
                color: (theme) => theme.palette.primary.main,
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                color: "text.primary",
                mr: 2,
              }}
            >
              {isComprovante ? "Comprovantes de pagamento" : "Notas fiscais"}
            </Typography>
          </Box>
          <Chip
            label={`Total de arquivos: ${
              comprovantes.filter((item) => item.type === type).length
            }`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>

        {comprovantes.length === 0 && (
          <Box
            sx={{
              display: "flex",
              mb: 5,
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "16px", color: "text.secondary" }}>
              {isComprovante
                ? "Nenhum comprovante de pagamento."
                : "Nenhuma nota fiscal."}
            </Typography>
          </Box>
        )}

        {comprovantes
          .filter((item) => item.type === type)
          .map((item) => {
            return (
              <CardPreviewFile
                key={item.original_name}
                type={item.mime_type as "image/jpeg"}
                size={item.size}
                created_at={item.created_at}
                isDownload={true}
                isView={true}
                titlePreview={handleIfLimitCharacter({
                  LIMIT: isBreakPoint ? 15 : 50,
                  value: item.original_name,
                })}
                title={item.original_name}
                url={item.url}
                onClick={
                  (isComprovante && !isProdutor) ||
                  (type === "NOTA_FISCAL" && isProdutor)
                    ? () => {
                        setIdModalDelete(item.id);
                        setOpenModalDelete(true);
                      }
                    : undefined
                }
              />
            );
          })}
      </Box>
    </>
  );
};
